import React, { useState, useRef } from 'react';
import {
  Upload, Music, Mic, X, Loader2, Volume2, VolumeX,
  ChevronDown, ChevronUp, Play, Pause, Square,
} from 'lucide-react';
import type { AudioTrack } from './types';
import { LibraryTrackList, type LibraryTrack } from './musicLibrary';

export interface AudioTrackRow {
  id: string;
  project_id: string;
  app_type: string;
  track_type: 'music' | 'voiceover';
  label: string | null;
  audio_url: string;
  audio_path: string;
  volume: number;
  start_offset_seconds: number;
  fade_in: boolean;
  fade_out: boolean;
  is_active: boolean;
  script_text: string | null;
  tts_voice_id: string | null;
  tts_provider: string | null;
  duration_seconds: number | null;
  created_at: string;
  updated_at: string;
}

export interface AudioTracksPanelProps {
  tracks: AudioTrackRow[];
  onUploadTrack: (file: File, trackType: 'music' | 'voiceover', label?: string) => Promise<void>;
  onUpdateTrack: (trackId: string, updates: Partial<Pick<AudioTrackRow, 'volume' | 'fade_in' | 'fade_out' | 'is_active' | 'label'>>) => Promise<void>;
  onDeleteTrack: (trackId: string) => Promise<void>;
  renderVoiceoverSection?: React.ReactNode;
  uploading?: boolean;
  libraryTracks?: LibraryTrack[];
  onUseLibraryTrack?: (track: LibraryTrack) => Promise<void> | void;
  libraryAddingId?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  supabase?: any;
}

export function AudioTracksPanel({
  tracks,
  onUploadTrack,
  onUpdateTrack,
  onDeleteTrack,
  renderVoiceoverSection,
  uploading = false,
  libraryTracks,
  onUseLibraryTrack,
  libraryAddingId,
  supabase,
}: AudioTracksPanelProps) {
  const [expandedSection, setExpandedSection] = useState<'music' | 'voiceover' | null>('music');
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const audioPreviewRef = useRef<HTMLAudioElement | null>(null);

  const musicTracks = tracks.filter((t) => t.track_type === 'music');
  const voiceoverTracks = tracks.filter((t) => t.track_type === 'voiceover');

  const togglePreview = (track: AudioTrackRow) => {
    if (previewingId === track.id) {
      audioPreviewRef.current?.pause();
      setPreviewingId(null);
      return;
    }

    if (audioPreviewRef.current) {
      audioPreviewRef.current.pause();
    }

    const audio = new Audio(track.audio_url);
    audio.volume = track.volume;
    audio.onended = () => setPreviewingId(null);
    audio.play().catch(() => {});
    audioPreviewRef.current = audio;
    setPreviewingId(track.id);
  };

  const handleMusicUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onUploadTrack(file, 'music', file.name.replace(/\.[^.]+$/, ''));
    e.target.value = '';
  };

  const handleVoiceoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onUploadTrack(file, 'voiceover', 'Recorded voiceover');
    e.target.value = '';
  };

  const renderTrackItem = (track: AudioTrackRow) => {
    const isPreviewing = previewingId === track.id;
    return (
      <div
        key={track.id}
        className={`bg-slate-800/50 rounded-lg p-3 space-y-2 border ${
          track.is_active ? 'border-purple-500/30' : 'border-slate-700/50 opacity-60'
        }`}
      >
        <div className="flex items-center gap-2">
          <button
            onClick={() => togglePreview(track)}
            className="w-7 h-7 rounded-full bg-purple-600 hover:bg-purple-500 flex items-center justify-center text-white transition-colors shrink-0"
          >
            {isPreviewing ? <Square size={10} /> : <Play size={12} className="ml-0.5" />}
          </button>

          <div className="flex-1 min-w-0">
            <p className="text-xs text-white truncate">
              {track.label || (track.track_type === 'music' ? 'Music track' : 'Voiceover')}
            </p>
            <p className="text-[10px] text-slate-500">
              {track.tts_provider === 'recording' ? 'Recording' :
               track.tts_provider === 'falai' ? 'AI Generated' :
               track.tts_provider === 'elevenlabs' ? 'ElevenLabs' :
               isPreviewing ? 'Playing...' : 'Click to preview'}
            </p>
          </div>

          <label className="flex items-center gap-1 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={track.is_active}
              onChange={(e) => onUpdateTrack(track.id, { is_active: e.target.checked })}
              className="w-3.5 h-3.5 rounded bg-slate-800 border-slate-700 text-purple-500 focus:ring-purple-500"
            />
            <span className="text-[10px] text-slate-500">Active</span>
          </label>

          <button
            onClick={() => onDeleteTrack(track.id)}
            className="p-1 text-slate-500 hover:text-red-400 transition-colors shrink-0"
          >
            <X size={14} />
          </button>
        </div>

        {track.is_active && (
          <>
            <div className="flex items-center gap-2">
              <Volume2 size={11} className="text-slate-500 shrink-0" />
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={track.volume}
                onChange={(e) => onUpdateTrack(track.id, { volume: parseFloat(e.target.value) })}
                className="flex-1 h-1 bg-slate-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500"
              />
              <span className="text-[10px] font-mono text-slate-500 w-8 text-right">
                {Math.round(track.volume * 100)}%
              </span>
            </div>

            <div className="flex gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={track.fade_in}
                  onChange={(e) => onUpdateTrack(track.id, { fade_in: e.target.checked })}
                  className="w-3 h-3 rounded bg-slate-800 border-slate-700 text-purple-500 focus:ring-purple-500"
                />
                <span className="text-[10px] text-slate-400">Fade in</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={track.fade_out}
                  onChange={(e) => onUpdateTrack(track.id, { fade_out: e.target.checked })}
                  className="w-3 h-3 rounded bg-slate-800 border-slate-700 text-purple-500 focus:ring-purple-500"
                />
                <span className="text-[10px] text-slate-400">Fade out</span>
              </label>
            </div>
          </>
        )}

        {track.script_text && (
          <p className="text-[10px] text-slate-500 italic line-clamp-2 border-t border-slate-700/50 pt-1.5 mt-1">
            "{track.script_text}"
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-4">
      {/* Music Section */}
      <div>
        <button
          onClick={() => setExpandedSection(expandedSection === 'music' ? null : 'music')}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <Music size={14} className="text-purple-400" />
            <h3 className="text-sm font-semibold text-white">Background Music</h3>
            {musicTracks.length > 0 && (
              <span className="text-[10px] bg-purple-500/20 text-purple-300 px-1.5 py-0.5 rounded-full">
                {musicTracks.filter((t) => t.is_active).length} active
              </span>
            )}
          </div>
          {expandedSection === 'music' ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
        </button>

        {expandedSection === 'music' && (
          <div className="mt-3 space-y-2">
            {musicTracks.map(renderTrackItem)}

            {libraryTracks && libraryTracks.length > 0 && onUseLibraryTrack && (
              <LibraryTrackList
                tracks={libraryTracks}
                addingId={libraryAddingId}
                onAdd={(track) => void onUseLibraryTrack(track)}
                supabase={supabase}
              />
            )}

            <label className="flex items-center gap-2 px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
              {uploading ? (
                <Loader2 size={14} className="animate-spin text-slate-400" />
              ) : (
                <Upload size={14} className="text-slate-400" />
              )}
              <span className="text-xs text-slate-400">
                {uploading ? 'Uploading...' : 'Upload music (MP3, WAV)'}
              </span>
              <input
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={handleMusicUpload}
                disabled={uploading}
              />
            </label>
          </div>
        )}
      </div>

      <div className="border-t border-slate-800" />

      {/* Voiceover Section */}
      <div>
        <button
          onClick={() => setExpandedSection(expandedSection === 'voiceover' ? null : 'voiceover')}
          className="w-full flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-2">
            <Mic size={14} className="text-blue-400" />
            <h3 className="text-sm font-semibold text-white">Voiceover / Script</h3>
            {voiceoverTracks.length > 0 && (
              <span className="text-[10px] bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded-full">
                {voiceoverTracks.filter((t) => t.is_active).length} active
              </span>
            )}
          </div>
          {expandedSection === 'voiceover' ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
        </button>

        {expandedSection === 'voiceover' && (
          <div className="mt-3 space-y-3">
            {voiceoverTracks.map(renderTrackItem)}

            {renderVoiceoverSection}

            <label className="flex items-center gap-2 px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors">
              {uploading ? (
                <Loader2 size={14} className="animate-spin text-slate-400" />
              ) : (
                <Upload size={14} className="text-slate-400" />
              )}
              <span className="text-xs text-slate-400">
                {uploading ? 'Uploading...' : 'Upload voiceover (MP3, WAV)'}
              </span>
              <input
                type="file"
                accept="audio/*"
                className="hidden"
                onChange={handleVoiceoverUpload}
                disabled={uploading}
              />
            </label>
          </div>
        )}
      </div>

      <div className="bg-slate-800/30 rounded-lg p-2">
        <p className="text-[10px] text-slate-500">
          Audio tracks play over the video without re-rendering. Change music or voiceover anytime.
        </p>
      </div>
    </div>
  );
}
